import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, Button, Input, Spinner, Table } from "reactstrap";

import ProductBatchRecallOffcanvas from "../../../../Components/Studio/ProductBatchRecallOffcanvas";
import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  getProductBatches,
  markProductBatchOpened,
  updateProductBatch,
} from "../../../../services/productService";
import { InventoryBatch } from "../../../../types/inventoryBatch";

interface ProductBatchesPanelProps {
  productId: number;
  openShelfLifeDays: number | null;
  canEdit: boolean;
  onChanged?: () => void;
}

const ProductBatchesPanel = ({
  productId,
  openShelfLifeDays,
  canEdit,
  onChanged,
}: ProductBatchesPanelProps) => {
  const { t, i18n } = useTranslation();
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [openedOnly, setOpenedOnly] = useState(false);
  const [overrideDaysByBatch, setOverrideDaysByBatch] = useState<
    Record<number, string>
  >({});
  const [recallBatch, setRecallBatch] = useState<InventoryBatch | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProductBatches(productId, {
        opened: openedOnly || undefined,
      });
      setBatches(response.data ?? []);
    } catch {
      toast.error(t("studio.products.batches.toast.loadError"));
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [openedOnly, productId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const formatDate = (value: string | null): string => {
    if (!value) {
      return t("studio.products.batches.noExpiry");
    }

    return new Intl.DateTimeFormat(i18n.language, { dateStyle: "short" }).format(
      new Date(value)
    );
  };

  const factoryExpiryBadge = (batch: InventoryBatch) => {
    if (batch.expires_at === null) {
      return (
        <Badge color="secondary" className="bg-secondary-subtle text-secondary">
          {t("studio.products.batches.noExpiry")}
        </Badge>
      );
    }

    const days = batch.expiring_within_days;
    if (days === null) {
      return <span>{formatDate(batch.expires_at)}</span>;
    }

    if (days < 0) {
      return <Badge color="danger">{t("studio.products.expiry.expired")}</Badge>;
    }

    if (days <= 7) {
      return (
        <Badge color="warning">
          {t("studio.products.expiry.expiresInDays", { days })}
        </Badge>
      );
    }

    if (days <= 30) {
      return (
        <Badge color="info">
          {t("studio.products.expiry.expiresInDays", { days })}
        </Badge>
      );
    }

    return <span>{formatDate(batch.expires_at)}</span>;
  };

  const openShelfBadge = (batch: InventoryBatch) => {
    if (!batch.is_opened || batch.open_expires_at === null) {
      return (
        <Badge color="light" className="text-muted">
          {t("studio.products.batches.openShelf.sealed")}
        </Badge>
      );
    }

    const days = batch.open_expiring_within_days;
    if (days === null) {
      return (
        <span>
          {t("studio.products.batches.openShelf.useBy", {
            date: formatDate(batch.open_expires_at),
          })}
        </span>
      );
    }

    if (days < 0) {
      return (
        <Badge color="danger">
          {t("studio.products.batches.openShelf.pastUseBy")}
        </Badge>
      );
    }

    if (days <= 3) {
      return (
        <Badge color="warning">
          {t("studio.products.batches.openShelf.useWithinDays", { days })}
        </Badge>
      );
    }

    return (
      <Badge color="info">
        {t("studio.products.batches.openShelf.useBy", {
          date: formatDate(batch.open_expires_at),
        })}
      </Badge>
    );
  };

  const handleExpiryChange = async (batch: InventoryBatch, value: string) => {
    setSavingId(batch.id);
    try {
      await updateProductBatch(productId, batch.id, {
        expires_at: value === "" ? null : value,
      });
      toast.success(t("studio.products.batches.toast.updated"));
      await load();
      onChanged?.();
    } catch (error: any) {
      toast.error(error?.data || t("studio.products.batches.toast.updateError"));
    } finally {
      setSavingId(null);
    }
  };

  const handleMarkOpened = async (batch: InventoryBatch) => {
    const overrideRaw = (overrideDaysByBatch[batch.id] ?? "").trim();
    const overrideDays =
      overrideRaw !== "" ? Number.parseInt(overrideRaw, 10) : null;

    if (
      (openShelfLifeDays === null || openShelfLifeDays < 1) &&
      (overrideDays === null || Number.isNaN(overrideDays) || overrideDays < 1)
    ) {
      toast.error(t("studio.products.batches.openShelf.daysRequired"));
      return;
    }

    setSavingId(batch.id);
    try {
      await markProductBatchOpened(productId, batch.id, {
        ...(overrideDays !== null && !Number.isNaN(overrideDays) && overrideDays >= 1
          ? { open_shelf_life_days: overrideDays }
          : {}),
      });
      toast.success(t("studio.products.batches.openShelf.toast.opened"));
      await load();
      onChanged?.();
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.products.batches.openShelf.toast.openError")
      );
    } finally {
      setSavingId(null);
    }
  };

  const defaultOverrideHint = useMemo(() => {
    if (openShelfLifeDays !== null && openShelfLifeDays > 0) {
      return t("studio.products.batches.openShelf.productDaysHint", {
        days: openShelfLifeDays,
      });
    }

    return t("studio.products.batches.openShelf.overrideHint");
  }, [openShelfLifeDays, t]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
        <div className="form-check mb-0">
          <Input
            id={`batches-opened-only-${productId}`}
            type="checkbox"
            className="form-check-input"
            checked={openedOnly}
            onChange={(event) => setOpenedOnly(event.target.checked)}
          />
          <label
            className="form-check-label"
            htmlFor={`batches-opened-only-${productId}`}
          >
            {t("studio.products.batches.filter.openedOnly")}
          </label>
        </div>
        <small className="text-muted">{defaultOverrideHint}</small>
      </div>

      {batches.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">
            {openedOnly
              ? t("studio.products.batches.openShelf.emptyOpened")
              : t("studio.products.batches.empty")}
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table className="align-middle table-nowrap mb-0">
            <thead className="table-light">
              <tr>
                <th>{t("studio.products.batches.table.quantity")}</th>
                <th>{t("studio.products.batches.table.factoryExpiry")}</th>
                <th>{t("studio.products.batches.table.openShelf")}</th>
                <th>{t("studio.products.batches.table.unitCost")}</th>
                <th>{t("studio.products.batches.table.received")}</th>
                <th>{t("studio.products.batches.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id}>
                  <td>{batch.quantity}</td>
                  <td>{factoryExpiryBadge(batch)}</td>
                  <td>{openShelfBadge(batch)}</td>
                  <td>
                    {formatMoneyFromCents(batch.cost_price_cents, i18n.language)}
                  </td>
                  <td>{formatDate(batch.received_at)}</td>
                  <td>
                    <div className="d-flex flex-column gap-2">
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 align-self-start"
                        onClick={() => setRecallBatch(batch)}
                      >
                        {t("studio.products.batches.recall.actions.open")}
                      </Button>
                      {canEdit ? (
                        <>
                          <div className="d-flex gap-2 align-items-center flex-wrap">
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 160 }}
                              aria-label={t(
                                "studio.products.batches.table.editFactoryExpiry"
                              )}
                              defaultValue={
                                batch.expires_at
                                  ? batch.expires_at.slice(0, 10)
                                  : ""
                              }
                              disabled={savingId === batch.id}
                              onBlur={(event) => {
                                const next = event.target.value;
                                const current = batch.expires_at
                                  ? batch.expires_at.slice(0, 10)
                                  : "";
                                if (next !== current) {
                                  void handleExpiryChange(batch, next);
                                }
                              }}
                            />
                            {batch.expires_at ? (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                disabled={savingId === batch.id}
                                onClick={() =>
                                  void handleExpiryChange(batch, "")
                                }
                              >
                                {t(
                                  "studio.products.batches.actions.clearExpiry"
                                )}
                              </Button>
                            ) : null}
                          </div>
                          {!batch.is_opened ? (
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                              <Input
                                type="number"
                                min={1}
                                max={3650}
                                bsSize="sm"
                                style={{ maxWidth: 110 }}
                                placeholder={t(
                                  "studio.products.batches.openShelf.daysPlaceholder"
                                )}
                                value={overrideDaysByBatch[batch.id] ?? ""}
                                disabled={savingId === batch.id}
                                onChange={(event) =>
                                  setOverrideDaysByBatch((prev) => ({
                                    ...prev,
                                    [batch.id]: event.target.value,
                                  }))
                                }
                              />
                              <Button
                                color="primary"
                                size="sm"
                                outline
                                disabled={savingId === batch.id}
                                onClick={() => void handleMarkOpened(batch)}
                              >
                                {t(
                                  "studio.products.batches.openShelf.markOpened"
                                )}
                              </Button>
                            </div>
                          ) : (
                            <small className="text-muted">
                              {t("studio.products.batches.openShelf.openedOn", {
                                date: formatDate(batch.opened_at),
                              })}
                            </small>
                          )}
                          {savingId === batch.id ? <Spinner size="sm" /> : null}
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <ProductBatchRecallOffcanvas
        isOpen={recallBatch !== null}
        productId={productId}
        batchId={recallBatch?.id ?? null}
        batchLabel={
          recallBatch
            ? t("studio.products.batches.recall.batchHint", {
                qty: recallBatch.quantity,
                received: formatDate(recallBatch.received_at),
              })
            : undefined
        }
        onClose={() => setRecallBatch(null)}
      />
    </div>
  );
};

export default ProductBatchesPanel;
